import type { FeatureFlag, FlagEvaluationContext } from "./types";

/**
 * Deterministic hash for percentage rollout.
 * Same user + flag key always produces the same bucket (0-99).
 */
function hashBucket(userId: string, flagKey: string): number {
  const str = `${userId}:${flagKey}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  return Math.abs(hash) % 100;
}

/**
 * Pure function: evaluates whether a feature flag is active for a given context.
 *
 * Evaluation order:
 * 1. Check dependencies (recursive, with cycle detection)
 * 2. Check environment
 * 3. Check enabled
 * 4. Check roles
 * 5. Check percentage rollout
 */
export function evaluateFlag(
  flag: FeatureFlag,
  ctx: FlagEvaluationContext,
  allFlags: ReadonlyMap<string, FeatureFlag>,
  _visited?: Set<string>,
): boolean {
  // Cycle detection
  const visited = _visited ?? new Set<string>();
  if (visited.has(flag.key)) return false;
  visited.add(flag.key);

  // 1. Dependencies — all must evaluate to true
  for (const depKey of flag.dependencies) {
    const depFlag = allFlags.get(depKey);
    if (!depFlag) return false;
    if (!evaluateFlag(depFlag, ctx, allFlags, visited)) return false;
  }

  // 2. Environment
  if (flag.environment !== "all" && flag.environment !== ctx.environment) {
    return false;
  }

  // 3. Enabled
  if (!flag.enabled) return false;

  // 4. Roles (empty = all roles allowed)
  if (flag.roles.length > 0 && !flag.roles.includes(ctx.role)) {
    return false;
  }

  // 5. Percentage rollout
  if (flag.percentage < 100) {
    if (flag.percentage <= 0) return false;
    const bucket = hashBucket(ctx.userId, flag.key);
    if (bucket >= flag.percentage) return false;
  }

  return true;
}
